import { Injectable } from '@angular/core';
import { FirebaseApp } from '@angular/fire/app';
import { Analytics, getAnalytics, isSupported, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { environment } from '../../../environments/environment';

type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private analyticsInstancePromise: Promise<Analytics | null> | null = null;

  constructor(
    private app: FirebaseApp
  ) {}

  trackPageView(path: string): void {
    this.safeLog('page_view', {
      page_path: path,
      app_surface: 'web',
      tenant_key: environment.tenantKey
    });
  }

  trackLoginSucceeded(role: string | null | undefined): void {
    this.safeLog('login', {
      method: 'password',
      user_role: role || 'unknown',
      app_surface: 'web',
      tenant_key: environment.tenantKey
    });
  }

  trackLoginFailed(reason: string): void {
    this.safeLog('login_failed', {
      reason: reason || 'unknown',
      app_surface: 'web',
      tenant_key: environment.tenantKey
    });
  }

  trackEvent(eventName: string, params: AnalyticsParams = {}): void {
    this.safeLog(eventName, {
      ...params,
      app_surface: 'web',
      tenant_key: environment.tenantKey
    });
  }

  setUserContext(userId: string | null | undefined, role: string | null | undefined): void {
    void this.withAnalytics((analytics) => {
      setUserId(analytics, userId || null);
      if (!userId) {
        return;
      }
      setUserProperties(analytics, {
        user_role: role || 'unknown',
        tenant_key: environment.tenantKey
      });
    });
  }

  private safeLog(eventName: string, params: AnalyticsParams): void {
    void this.withAnalytics((analytics) => {
      logEvent(analytics, eventName, params);
    });
  }

  private async withAnalytics(operation: (analytics: Analytics) => void): Promise<void> {
    try {
      const analytics = await this.getAnalyticsInstance();
      if (!analytics) {
        return;
      }
      operation(analytics);
    } catch {
      // Ignore analytics failures to avoid impacting user flows.
    }
  }

  private getAnalyticsInstance(): Promise<Analytics | null> {
    if (!this.analyticsInstancePromise) {
      this.analyticsInstancePromise = (async () => {
        if (environment.enableAnalytics === false) {
          return null;
        }
        const supported = await isSupported();
        if (!supported) {
          return null;
        }
        return getAnalytics(this.app);
      })();
    }
    return this.analyticsInstancePromise;
  }
}
